import { booleanAttribute } from "#ce/utils/props";
import { providerNames } from "./composables/usePayment";

export const props = Object.freeze({
  /**
   * Payment provider that backs the element. Each provider brings its own SDK and its own set of
   * relevant props.
   *
   * @type {String}
   * @values stripe, instamed
   */
  provider: {
    type: String,
    required: true,
    validator: (value) => Object.keys(providerNames).includes(value),
  },
  /**
   * Stripe Elements appearance object, passed straight through to the SDK. Set as a DOM property.
   *
   * @type {Object}
   */
  appearance: {
    type: Object,
    default: () => ({}),
    external: true,
  },
  /**
   * Provider account key: the Stripe publishable key, or the Instamed account ID.
   *
   * @type {String}
   * @default undefined
   */
  publishableKey: {
    type: String,
    default: undefined,
  },
  /**
   * Instamed environment the hosted scripts run against.
   *
   * @type {String}
   * @default undefined
   */
  environment: {
    type: String,
    default: undefined,
  },
  /**
   * Organization shown to the payer in the Instamed disclaimer.
   *
   * @type {String}
   * @default undefined
   */
  organizationName: {
    type: String,
    default: undefined,
  },
  /**
   * Secret of a payment intent created on the server. Changing it tears the mounted element down
   * and builds a new one against the new intent.
   *
   * @type {String}
   * @default undefined
   */
  clientSecret: {
    type: String,
    default: undefined,
  },
  /**
   * Endpoint the Instamed card token is captured against.
   *
   * @type {String}
   * @default undefined
   */
  captureUrl: {
    type: String,
    default: undefined,
  },
  /**
   * URL the provider returns the payer to once the payment is confirmed.
   *
   * @type {String}
   * @default undefined
   */
  returnUrl: {
    type: String,
    default: undefined,
  },
  /**
   * Hide the built-in pay button, for hosts that drive payment from their own control through the
   * exposed `validate()`, `confirmPayment()`, and `validateAndConfirm()` methods.
   *
   * @type {Boolean|String}
   * @default false
   */
  hidePayButton: booleanAttribute(),
  /**
   * Hide the built-in error alert, for hosts that render payment errors themselves from the
   * `error` event.
   *
   * @type {Boolean|String}
   * @default false
   */
  hideError: booleanAttribute(),
});
